import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';

import { Button, Checkbox } from '../../components/_shared';
import AccountLayout from '../../components/layout/Account';
import { makeSelectUser } from '../../store/selectors';
import { getUserSettings, updateUserSettings } from '../../service/user';
import { notify } from '../../utils/helper';
import { ThemeContext } from '../../providers/ThemeProvider';
import { OrgSettingsContainer } from './styled';
import { uploadToS3 } from '../../utils/helper';
import { isEnterprise } from '../../config';

const Settings = () => {
  const user = useSelector(makeSelectUser());
  const [settings, setSettings] = useState({});
  const {
    headerColor,
    logo,
    setLogo,
    setHeaderColor,
    primaryColor,
    setPrimaryColor,
  } = useContext(ThemeContext);
  const [iframeKey, setIframeKey] = useState(0);
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserSetting();
    }
  }, [user]);

  const fetchUserSetting = async () => {
    try {
      const res = await getUserSettings();
      setSettings(res.data.data);
    } catch (err) {
      notify(
        'error',
        'Failed to fetch user settings: ' + (err && err.toString())
      );
    }
  };

  const handleSaveSetting = (key) => async () => {
    try {
      await updateUserSettings({ ...settings, [key]: !settings[key] });
      notify('success', 'Updated user setting successfully!');
      setSettings({
        ...settings,
        [key]: !settings[key],
      });
    } catch (err) {
      notify(
        'error',
        'Failed to update user setting: ' + (err && err.toString())
      );
    }
  };

  useEffect(() => setIframeKey((v) => v + 1), [
    primaryColor,
    headerColor,
    logo,
  ]);

  const handleDropFile = (files) => {
    setUploadFile(files[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDropFile,
  });

  const handleUploadLogo = async () => {
    setIsUploading(true);

    const res = await uploadToS3(uploadFile, 'logos');

    if (res.status === 204) {
      setLogo(uploadFile.name);
    } else {
      notify('error', 'Failed to upload logo. Please try again later.');
    }

    setIsUploading(false);
  };
  const handleCancelUpload = () => {
    setUploadFile(null);
  };

  return (
    <AccountLayout>
      <div className="text-lg font-semibold text-black tracking-widest sm:text-xl sm:ml-4">
        Settings
      </div>
      <div className="p-4 mt-3">
        <div className="flex items-center">
          <Checkbox
            checked={!settings.unsubscribeQA}
            label="Email me Q&A notifications for topics I participate in"
            onClick={handleSaveSetting('unsubscribeQA')}
          />
        </div>
        {!isEnterprise && (
          <div className="flex items-center">
            <Checkbox
              checked={settings.optedIn}
              label="Opt in to Career Connections Program"
              onClick={handleSaveSetting('optedIn')}
            />
          </div>
        )}

        {user?.role === 'admin' && (
          <div className="mt-12">
            <div className="text-lg font-semibold text-black">
              Organization Settings
            </div>
            <OrgSettingsContainer className="mt-4">
              <div className="org-theme">
                <div className="text-center text-xl font-semibold">
                  - Theme -
                </div>
                <div className="flex justify-center my-5">
                  <div className="flex items-center mr-5">
                    <input
                      type="color"
                      id="bg-color"
                      name="bg-color"
                      value={headerColor}
                      onChange={(e) => setHeaderColor(e.target.value)}
                    />
                    <label for="bg-color" className="ml-3 text-black">
                      Header Color
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="color"
                      id="bg-color"
                      name="bg-color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                    />
                    <label for="bg-color" className="ml-3 text-black">
                      Card Bg Color
                    </label>
                  </div>
                </div>
              </div>
              <div className="org-logo">
                <div className="text-center text-xl font-semibold mb-3">
                  - Organization Logo{' '}
                  <span className="text-sm font-normal">(Size: 150 x 44)</span>{' '}
                  -
                </div>
                <div className="flex items-center">
                  <div
                    {...getRootProps({
                      className:
                        'cursor-pointer text-center p-2 border-2 border-dashed border-gray-500 text-gray-500 hover:text-gray-200 hover:border-gray-200 w-full',
                    })}
                  >
                    <input
                      {...getInputProps()}
                      accept="image/png, image/gif, image/jpeg, image/ico"
                    />
                    <p>
                      {uploadFile
                        ? uploadFile.name
                        : 'Drag and drop logo image here or click to select file'}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <Button
                      className="mx-2 px-2 py-2"
                      disabled={!uploadFile || isUploading}
                      caption={isUploading ? 'Uploading...' : 'Upload'}
                      onClick={handleUploadLogo}
                    />
                    <Button
                      className="px-2 py-2"
                      caption="Cancel"
                      onClick={handleCancelUpload}
                    />
                  </div>
                </div>
              </div>
            </OrgSettingsContainer>
            <div className="mr-4">Preview</div>
            <div className="bg-white p-4">
              <iframe
                key={iframeKey}
                src="/programs?iframe=1"
                width="100%"
                height={500}
              />
            </div>
          </div>
        )}
      </div>
    </AccountLayout>
  );
};

export default Settings;
